import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { api } from "@lr-notitia/notitia-designer/client";
import { graphlyGraph } from "../modules/workspaces/utils/graph";
import type {
	GraphModification,
	TemplateWorkspace,
	Positions,
	LanguageCode,
} from "@lr-notitia/notitia-designer/schemas";
import { handleGraphModification } from "./graph/utils";
import { useGraphStore } from "./graph";
import { useDomainStore } from "./domain";
import { useTeamlyStore } from "./teamly";
import { useSymbolsStore } from "./symbols";
import type { Link } from "@livereader/graphly-d3";
import { UserRole } from "@lr-notitia/notitia-designer/schemas";
import { isTemplateWorkspace } from "@lr-notitia/notitia-model-store";

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let teamlyStore: ReturnType<typeof useTeamlyStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	teamlyStore = useTeamlyStore();
	symbolsStore = useSymbolsStore();
}

export type ElementType = "data-object" | "link";

export const useTemplateWorkspacesStore = defineStore("TemplateWorkspaces", () => {
	init();
	let domainId = "";
	const workspaces = ref<TemplateWorkspace[]>([]);

	let loaded = ref<boolean>(false);

	function loadWorkspaces(dId: string): Promise<void> {
		domainId = dId;
		return new Promise(async (resolve) => {
			workspaces.value = sortAlphabetically(
				Object.values(graphStore.graph.nodes).filter((n) => isTemplateWorkspace(n)) as TemplateWorkspace[]
			);
			await Promise.all(workspaces.value.map((workspace) => getNodePositions(workspace.id)));
			resolve();
			loaded.value = true;
			api()
				.socket()
				.domain(domainId)
				.template()
				.workspaces()
				.on((message) => {
					switch (message.method) {
						case "create":
							if (!workspaces.value.find((d) => d.id === message.data.id))
								workspaces.value.push(message.data);
							break;
						case "update":
							workspaces.value = workspaces.value.map((workspace) => {
								if (workspace.id === message.data.id) return message.data;
								return workspace;
							});
							break;
						case "delete":
							workspaces.value = workspaces.value.filter((workspace) => workspace.id !== message.data);
							break;
					}
				});
		});
	}
	function clearWorkspaces() {
		api().socket().domain(domainId).template().workspaces().off();
		workspaces.value = [];
		domainId = "";
	}

	function getById(id: string): TemplateWorkspace | undefined {
		return workspaces.value?.find((workspace) => workspace.id === id);
	}

	function addWorkspace(
		name: string,
		description: string = "",
		tags: string[],
		password: string,
		language: LanguageCode = "en"
	): Promise<GraphModification> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.template()
				.workspaces()
				.post(name, description, tags, password, language)
				.then((data) => {
					handleGraphModification(data);
					resolve(data);
				})
				.catch(() => reject());
		});
	}
	function updateWorkspace(workspace: TemplateWorkspace): Promise<TemplateWorkspace> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.template()
				.workspace(workspace.id)
				.put(workspace)
				.then((data) => {
					handleGraphModification(data);
					resolve(data[workspace.id] as TemplateWorkspace);
				})
				.catch(() => reject());
		});
	}
	function removeWorkspace(id: string): Promise<void> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			let workspace = getById(id);
			if (!workspace) return reject();
			workspace.dataobjects.forEach((dataobjectId) => {
				graphStore.template.dataobject.remove(dataobjectId);
			});

			api()
				.domain(domainId)
				.template()
				.workspace(id)
				.del()
				.then((data) => {
					handleGraphModification(data);
					resolve();
				})
				.catch(() => reject());
		});
	}

	function removeElement(workspaceId: string, type: ElementType, node: any): Promise<GraphModification> {
		if (!graphStore || !domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			const workspace = getById(workspaceId);
			if (!workspace) return reject();
			switch (type) {
				case "data-object": {
					workspace.dataobjects = workspace.dataobjects.filter((d) => d !== node.id);
					let parentDataobject = workspace.dataobjects
						.map((d) => graphStore.template.dataobject.byId(d))
						.find((d) => d?.hasA.includes(node.id));

					if (parentDataobject) {
						parentDataobject.hasA = parentDataobject.hasA.filter((h) => h !== node.id);
						api()
							.domain(domainId)
							.template()
							.dataobject(parentDataobject.id)
							.put(parentDataobject)
							.then((data) => {
								handleGraphModification(data);
							});
					}
					Promise.all([
						api().domain(domainId).template().workspace(workspace.id).put(workspace),
						graphStore.template.dataobject.remove(node.id),
					]).then((data) => {
						handleGraphModification(data[0]);
						resolve(data[0]);
					});

					break;
				}
			}
		});
	}
	function removeDataobjectLink(link: Link): Promise<GraphModification> {
		if (!graphStore || !domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			let sourceDataobject = graphStore.template.dataobject.byId(
				typeof link.source == "string" ? link.source : link.source.id
			);
			if (!sourceDataobject) return reject();
			sourceDataobject.hasA = sourceDataobject.hasA.filter(
				(id) => id !== (typeof link.target === "string" ? link.target : link.target.id)
			);
			api()
				.domain(domainId)
				.graphs()
				.patch({ [sourceDataobject.id]: sourceDataobject })
				.then((data) => {
					handleGraphModification(data);
					resolve(data);
				})
				.catch(() => reject());
		});
	}

	const openedWorkspaceId = ref<string | undefined>(undefined);
	const openedWorkspace = computed(() => {
		if (!openedWorkspaceId.value) return undefined;
		return getById(openedWorkspaceId.value);
	});
	const workspaceGraph = computed(() => graphlyGraph(openedWorkspaceId.value || ""));
	const nodePositions = ref<{ [workspaceId: string]: Positions }>({});

	function getNodePositions(workspaceId: string) {
		if (!graphStore) init();
		let workspace = getById(workspaceId);
		if (!workspace) return;
		workspace.dataobjects.forEach((dataobjectId) => {
			let template = graphStore.template.dataobject.byId(dataobjectId);
			if (!template) return;
			if (!workspaceId) return;
			if (!template.context) template.context = {};
			if (!template.context["nta-pos"]) template.context["nta-pos"] = {};
			if (!template.context["nta-pos"][workspaceId]) template.context["nta-pos"][workspaceId] = {};
			const pos = template.context["nta-pos"][workspaceId];
			if (!nodePositions.value[workspaceId]) nodePositions.value[workspaceId] = {};
			nodePositions.value[workspaceId][template.id] = {
				x: pos?.x || 0,
				y: pos?.y || 0,
				scale: pos?.scale || 1,
			};
		});
	}

	function sortAlphabetically(arr: TemplateWorkspace[]) {
		if (!symbolsStore) init();
		return arr.sort((a, b) => {
			let termA = symbolsStore.term({ id: a.refersTo, ...(a.terminology ? { terminology: a.terminology } : {}) });
			if (termA === "")
				termA = symbolsStore.term({
					id: a.refersTo,
					...(a.terminology ? { terminology: a.terminology } : {}),
					lang: "en",
				});
			let termB = symbolsStore.term({ id: b.refersTo, ...(b.terminology ? { terminology: b.terminology } : {}) });
			if (termB === "")
				termB = symbolsStore.term({
					id: b.refersTo,
					...(b.terminology ? { terminology: b.terminology } : {}),
					lang: "en",
				});

			if (termA < termB) return -1;
			else if (termA > termB) return 1;
			else return 0;
		});
	}

	function getPassword(workspaceId: string): Promise<string> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.template()
				.workspace(workspaceId)
				.password()
				.get()
				.then((data) => {
					resolve(data);
				})
				.catch(() => reject());
		});
	}
	function setPassword(workspaceId: string, password: string): Promise<void> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.template()
				.workspace(workspaceId)
				.password()
				.put(password)
				.then(() => {
					resolve();
				})
				.catch(() => reject());
		});
	}
	function validatePassword(workspaceId: string, password: string): Promise<boolean> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.template()
				.workspace(workspaceId)
				.password()
				.post(password)
				.then((data) => {
					resolve(data);
				})
				.catch(() => reject());
		});
	}

	watch(
		() => workspaces.value,
		(newVal) => {
			workspaces.value = sortAlphabetically(newVal);
		}
	);
	watch(
		() => workspaces.value.length,
		() => (workspaces.value = sortAlphabetically(workspaces.value))
	);
	watch(
		() => openedWorkspaceId.value,
		(newId, oldId) => {
			if (oldId) {
				api().socket().domain(domainId).template().workspace(oldId).off();
				api()
					.socket()
					.domain(domainId)
					.template()
					.workspace(openedWorkspaceId.value || "")
					.livePosition()
					.off();
			}
			if (newId && loaded.value) {
				api()
					.socket()
					.domain(domainId)
					.template()
					.workspace(newId)
					.on((message) => {
						const pt = workspaces.value?.find((workspace) => workspace.id === message.id);
						if (pt) {
							pt.refersTo = message.refersTo;
							pt.reviewRequired = message.reviewRequired;
							pt.dataobjects = message.dataobjects;
						}
					});
				api()
					.socket()
					.domain(domainId)
					.template()
					.workspace(openedWorkspaceId.value || "")
					.livePosition()
					.on((pos) => {
						nodePositions.value[newId] = pos;
					});
				api()
					.socket()
					.domain(domainId)
					.template()
					.workspace(openedWorkspaceId.value || "")
					.nodePosition()
					.on((pos) => {
						nodePositions.value[newId] = pos;
					});

				if (!teamlyStore) init();
				teamlyStore.getNotes(newId);
			}
		}
	);

	return {
		workspaces,
		loadWorkspaces,
		clearWorkspaces,
		getById,
		nodePositions,
		addWorkspace,
		updateWorkspace,
		removeWorkspace,
		removeElement,
		removeDataobjectLink,
		openedWorkspaceId,
		openedWorkspace,
		workspaceGraph,
		sortAlphabetically,
		getNodePositions,
		getPassword,
		setPassword,
		validatePassword,
	};
});
